import {createSlice} from "@reduxjs/toolkit";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";

/**
 * common initial state of all dialogs states in this slice.
 */
const commonInitialState = {
    open: false,
    mandatory: false,
}

/**
 * Initial state related to terms dialog
 */
export const reduxStoreWhatsNewDialogInitialState = {
    ...commonInitialState,
    data: null,
    onClose: null,
}

/**
 * Initial state related to the task more information dialog
 */
export const reduxStoreTaskInformationInitialState = {
    ...commonInitialState,
    task: null,
    setTask: () => void 0,
    toggleTaskDoneStatus: () => void 0,
    page: null,
}

export const reduxStoreUpsertTaskInitialState = {
    ...commonInitialState,
    page: null,
    onSuccess: () => void 0,
    title: null,
    task: null,
    isNCRTask: false,
}

export const reduxStoreUpsertNoteInitialState = {
    ...commonInitialState,
    tagsList: [],
    onSuccess: () => void 0,
    note: null,
    defaultIsPrivate: true,
    titleSuggestions: [],
}

export const reduxStoreWelcomePanelTaskInitialState = {
    ...commonInitialState,
    onClose: null
}

export const reduxStoreRemoveConfirmationInitialState = {
    ...commonInitialState,
    data: [],
    removedEntryTitle: null,
}

export const reduxStoreConfirmationInitialState = {
    ...commonInitialState,
    title: "Confirmation",
    description: "Are you sure you want to proceed?",
    confirmButtonText: "Confirm",
    cancelButtonText: "Cancel",
    otherButtons: [],
}

export const reduxStoreEditBizFormInitialState = {
    ...commonInitialState,
    formReference: null,
    formApiResolver: null,
}

export const reduxStoreViewInstallationGuideInitialState = {
    ...commonInitialState,
}

/**
 * Initial state of the dialogs slice.
 */
const dialogsInitialState = {
    [ReduxStoreRoutes.dialogs.whatsNew]: reduxStoreWhatsNewDialogInitialState,
    [ReduxStoreRoutes.dialogs.quickOrders]: commonInitialState,
    [ReduxStoreRoutes.dialogs.taskMoreInformation]: reduxStoreTaskInformationInitialState,
    [ReduxStoreRoutes.dialogs.upsertTask]: reduxStoreUpsertTaskInitialState,
    [ReduxStoreRoutes.dialogs.upsertNote]: reduxStoreUpsertNoteInitialState,
    [ReduxStoreRoutes.dialogs.welcomePanelTaskDialog]: reduxStoreWelcomePanelTaskInitialState,
    [ReduxStoreRoutes.dialogs.removeConfirmation]: reduxStoreRemoveConfirmationInitialState,
    [ReduxStoreRoutes.dialogs.confirmation]: reduxStoreConfirmationInitialState,
    [ReduxStoreRoutes.dialogs.editBizForm]: reduxStoreEditBizFormInitialState,
    [ReduxStoreRoutes.dialogs.viewInstallationGuide]: reduxStoreViewInstallationGuideInitialState,
}

/**
 * Reducer that is responsible for handling shared dialogs' states of the entire app.
 */
const reduxStoreDialogsSlice = createSlice({
    name: ReduxStoreRoutes.dialogs.base,
    initialState: dialogsInitialState,
    reducers: {
        /**
         * Sets the state of the chosen dialog in the application.
         *
         * the payload given to this method must contain the following:
         * ```json
         *  {
         *      type: string
         *      state: Record<string, any>
         *  }
         * ```
         * @param state
         * @param action
         */
        setDialogState: (state, action) => {
            let initialState = {};
            const path = action.payload.type;
            switch (action.payload.type) {
                case ReduxStoreRoutes.dialogs.whatsNew:
                    initialState = reduxStoreWhatsNewDialogInitialState;
                    break;
                case ReduxStoreRoutes.dialogs.quickOrders:
                    initialState = commonInitialState;
                    break;
                case ReduxStoreRoutes.dialogs.taskMoreInformation:
                    initialState = reduxStoreTaskInformationInitialState;
                    break;
                case ReduxStoreRoutes.dialogs.upsertTask:
                    initialState = reduxStoreUpsertTaskInitialState;
                    break;
                case ReduxStoreRoutes.dialogs.upsertNote:
                    initialState = reduxStoreUpsertNoteInitialState;
                    break;
                case ReduxStoreRoutes.dialogs.welcomePanelTaskDialog:
                    initialState = reduxStoreWelcomePanelTaskInitialState;
                    break;
                case ReduxStoreRoutes.dialogs.removeConfirmation:
                    initialState = reduxStoreRemoveConfirmationInitialState;
                    break;
                case ReduxStoreRoutes.dialogs.confirmation:
                    initialState = reduxStoreConfirmationInitialState;
                    break;
                case ReduxStoreRoutes.dialogs.editBizForm:
                    initialState = reduxStoreEditBizFormInitialState;
                    break;
                case ReduxStoreRoutes.dialogs.viewInstallationGuide:
                    initialState = reduxStoreViewInstallationGuideInitialState;
                    break;
                default:
                    return;
            }
            state[path] = {
                ...state[path],
                ...action.payload.state,
            }
            // when opened, we want to reset the dialog's state to the initial state, but keep the values given in the payload (like onClose callback, title, etc..)
            if (action.payload.state.open === true) {
                state[path] = {
                    ...initialState,
                    ...state[path],
                }
            }
            return state;
        },

    }
});

export default reduxStoreDialogsSlice;
